import React, { useEffect, useState } from "react";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { ManageAccounts, RemoveCircle, Sync } from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import { InformationSnippet } from "../../../shared-components/InformationSnippet";
import { OffboardSchoolDialog } from "../components/OffboardSchoolDialog";
import { SyncSchoolDialog } from "../components/SyncSchoolDialog";
import { formatDistanceToNow } from "date-fns";
import { SchoolSyncsAccordion } from "../components/SchoolSyncsAccordion";
import SchoolClassManager from "../components/SchoolClassManager";
import { RequestAccessDialog } from "../components/RequestAccessDialog";
import { FruitifyUserSearch } from "../components/FruitifyUserSearch";
import { FruitifyMetrics } from "../components/FruitifyMetrics";
import { SchoolConfigDialog } from "../components/SchoolConfigDialog";
import { useSchoolSubscriptionTypes } from "../hooks/useSchoolSubscriptionTypes";
import SubscriptionStatus from "../components/SubscriptionStatus";
import { LicenseUsageTable } from "../components/LicenseUsageTable";

import SchoolWorkflowEnrollmentDialog from "../onboarding/SchoolWorkflowEnrollmentDialog";

const SchoolMetricsAccordion = ({ school, disabled }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ mt: 2 }}>
      <Accordion expanded={expanded} variant={"outlined"} disabled={disabled}>
        <AccordionSummary onClick={() => setExpanded(!expanded)}>
          Metrics
        </AccordionSummary>

        <FruitifyMetrics school={school} />
      </Accordion>
    </Box>
  );
};

const SchoolViewSummary = ({
  school,
  onOffboardClicked,
  syncing,
  setSyncing,
  onUpdate = () => {},
}) => {
  const ApiClient = useApiClient();
  const [open, setOpen] = useState(false);
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [wondeMenuAnchor, setWondeMenuAnchor] = useState(null);
  const handleOpenWondeMenu = (event) => {
    setWondeMenuAnchor(event.currentTarget);
  };
  const handleCloseWondeMenu = () => {
    setWondeMenuAnchor(null);
  };
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const totalLicencesAllowed =
    school?.subscription?.numberOfLicencesAllowed || 0;
  const totalLicencesUsed =
    school?.subscription?.actualNumberOfLicences ?? "Not available";
  const licencePairs = school?.subscription?.yearGroupLicences || [];

  const fetchClassesFromWonde = async () => {
    try {
      const response = await ApiClient.wonde.fetchSchoolClasses({
        school: school.id,
      });
      if (response && response.success) {
        enqueueSnackbar(`Triggered class sync successfully`, {
          variant: "success",
          autoHideDuration: 3000,
        });
        setSyncing(true);
      } else {
        enqueueSnackbar(response?.message || `Failed to trigger class sync`, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to fetch question", error);
    }
  };

  const licensedSubjects = Array.from(
    new Map(
      (licencePairs || [])
        .flatMap((p) =>
          (p.subjects || []).map((subject) => [subject.id, subject]),
        )
        .filter(Boolean),
    ).values(),
  ).sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));

  const licensedYearGroups = Array.from(
    new Map(
      (licencePairs || [])
        .map((p) => [p.yearGroup?.id, p.yearGroup])
        .filter(Boolean),
    ).values(),
  ).sort((a, b) => (a?.yearGroup || 0) - (b?.yearGroup || 0));

  return (
    <Card data-testid={`school-summary-${school.id}`}>
      {enrollmentOpen && (
        <SchoolWorkflowEnrollmentDialog
          api={ApiClient.marketing}
          schools={[school]}
          onClose={() => setEnrollmentOpen(false)}
        />
      )}
      <SyncSchoolDialog
        school={school}
        open={syncDialogOpen}
        setOpen={setSyncDialogOpen}
        onSyncStart={() => setSyncing(true)}
      />
      <RequestAccessDialog
        open={open}
        setOpen={setOpen}
        initialSchool={school}
      />
      <SchoolConfigDialog
        open={manageDialogOpen}
        setOpen={setManageDialogOpen}
        school={school}
        onComplete={onUpdate}
      />
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h3">{school.name}</Typography>
          <Stack alignItems={"flex-end"} spacing={1}>
            <Stack spacing={2} direction={"row"}>
              <Button
                startIcon={<RemoveCircle />}
                variant="outlined"
                onClick={onOffboardClicked}
                disabled={!school.subscriptionStatus}
              >
                Offboard
              </Button>
              {syncing ? (
                <GeneralLoading
                  message={"Syncing..."}
                  direction={"row"}
                  size={30}
                />
              ) : school.wondeId ? (
                <>
                  <Button
                    startIcon={<Sync />}
                    variant="contained"
                    onClick={handleOpenWondeMenu}
                  >
                    Wonde Actions
                  </Button>

                  <Menu
                    anchorEl={wondeMenuAnchor}
                    open={Boolean(wondeMenuAnchor)}
                    onClose={handleCloseWondeMenu}
                  >
                    <MenuItem
                      onClick={() => {
                        handleCloseWondeMenu();
                        fetchClassesFromWonde();
                      }}
                    >
                      Fetch classes
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        handleCloseWondeMenu();
                        setSyncDialogOpen(true);
                      }}
                    >
                      Sync
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button
                  startIcon={<Sync />}
                  variant="contained"
                  onClick={() => setOpen(true)}
                >
                  Request Access
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => setEnrollmentOpen(true)}
              >
                Enroll to workflow
              </Button>
              <Button
                startIcon={<ManageAccounts />}
                variant="contained"
                onClick={() => setManageDialogOpen(true)}
              >
                Subscription
              </Button>
            </Stack>

            {school?.wondeLastSynced && !syncing && (
              <Typography variant="body2" color="text.secondary">
                Last sync{" "}
                {formatDistanceToNow(new Date(school.wondeLastSynced), {
                  addSuffix: true,
                })}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Grid container spacing={2} direction={"row"} sx={{ mt: 2, mb: 2 }}>
          <Grid size={4}>
            <InformationSnippet
              title={"Trial status"}
              valueComponent={
                <SubscriptionStatus
                  status={school.subscriptionStatus || "Not subscribed"}
                  approved={school.subscription?.isSchoolApproved || false}
                />
              }
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Teachers"}
              value={school.teachersCount || 0}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Students"}
              value={school.studentsCount || 0}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Total licences"}
              value={`${totalLicencesUsed} / ${totalLicencesAllowed}`}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Enabled subjects"}
              value={licensedSubjects.map((s) => {
                return { tag: s.name };
              })}
              useTagBox
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Licensed year groups"}
              value={licensedYearGroups.map((yg) => {
                return {
                  tag: `Year ${yg.yearGroup}`,
                };
              })}
              useTagBox
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default function SchoolView() {
  const { id } = useParams();
  const ApiClient = useApiClient();
  const [loading, setLoading] = useState(true);
  const [offboardDialogOpen, setOffboardDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [school, setSchool] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [yearGroups, setYearGroups] = useState([]);

  useEffect(() => {
    fetchSchool();
  }, [id]);

  const { subscriptions } = useSchoolSubscriptionTypes(ApiClient);

  const fetchSchool = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.schools.getSchool({
        school: id,
      });
      if (response) {
        setSchool(response.school);
      }
    } catch (error) {
      console.error("Failed to fetch question", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadCore = async () => {
      try {
        const res = await ApiClient.core.getSubjectsAndLevels();
        setSubjects(res?.subjects ?? []);
        const ygs = Array.from(
          new Map(
            (res?.educationLevels ?? [])
              .flatMap((lvl) => lvl.yearGroups ?? [])
              .map((yg) => [yg.id, yg]),
          ).values(),
        );
        setYearGroups(ygs);
      } catch (e) {
        console.error(e);
      }
    };
    loadCore();
  }, []);

  useEffect(() => {
    if (school.syncs?.length > 0) {
      setSyncing(school.syncs[0].inProgress);
    }
  }, [school]);

  const handleSaveLicences = async (items) => {
    try {
      const result = await ApiClient.schools.updateSchoolSubscriptionLicences({
        school: school.id,
        items,
      });
      if (result?.success) {
        const refreshed = await ApiClient.schools.getSchool({
          school: school.id,
        });
        setSchool(refreshed.school);
        enqueueSnackbar("Licences saved", { variant: "success" });
      } else {
        enqueueSnackbar(result?.message || "Failed to save licences", {
          variant: "error",
        });
      }
    } catch (e) {
      enqueueSnackbar("Failed to save licences", { variant: "error" });
    }
  };

  function updateSchoolSyncList(prevSchool, newSync) {
    const existingIndex = prevSchool.syncs?.findIndex(
      (s) => s.id === newSync.id,
    );

    let updatedSyncs;
    if (existingIndex !== undefined && existingIndex >= 0) {
      // Replace the existing sync with the new one
      updatedSyncs = [...prevSchool.syncs];
      updatedSyncs[existingIndex] = newSync;
    } else if (newSync) {
      // Add new sync to the top
      updatedSyncs = [newSync, ...(prevSchool.syncs || [])];
    }

    return {
      ...prevSchool,
      syncs: updatedSyncs || [],
      wondeLastSynced: new Date(),
    };
  }

  useEffect(() => {
    if (!syncing) return;
    const wondeSyncPoll = ApiClient.wonde.pollWondeSync({
      wondeSync: null,
      school: id,
      pollInterval: 5000,
      onUpdate: (data) =>
        setSchool((prev) => updateSchoolSyncList(prev, data.wondeSync)),
    });

    return () => wondeSyncPoll.unsubscribe();
  }, [syncing, id]);

  if (loading) {
    return <GeneralLoading />;
  }

  return (
    <FruitifyContainer>
      <OffboardSchoolDialog
        school={school}
        open={offboardDialogOpen}
        setOpen={setOffboardDialogOpen}
      />
      <Stack spacing={2}>
        <SchoolViewSummary
          school={school}
          onOffboardClicked={() => setOffboardDialogOpen(true)}
          setSyncing={setSyncing}
          syncing={syncing}
          onUpdate={fetchSchool}
        />
        <SchoolMetricsAccordion
          school={school}
          disabled={!school.isSubscribed}
        />
        <SchoolClassManager
          school={school}
          subjects={subjects}
          yearGroups={yearGroups}
        />
        <LicenseUsageTable
          initialRows={school?.subscription?.yearGroupLicences || []}
          subjects={subjects}
          yearGroups={yearGroups}
          onSave={handleSaveLicences}
          disabled={!school?.subscription}
          licenseOptions={subscriptions}
        />
        <SchoolSyncsAccordion
          syncs={school.syncs}
          disabled={!school.wondeLastSynced}
        />
        <FruitifyUserSearch
          showTitle
          hideSubscriptionStatus
          allowSelect
          allowEmailStatusFilter
          school={school}
          teacherOnly
        />
      </Stack>
    </FruitifyContainer>
  );
}

export { SchoolView };
